import { z } from 'zod'
import type {
  FilterOperator,
  FilterValues,
} from '../components/problems/types.js'
import { generateSlugFromName } from './slug.js'

/**
 * Convert a display name (list name, topic name) to a URL-safe slug.
 *
 * `generateSlugFromName` semantics — whitespace is the only word separator —
 * plus the sentinel '__all__', which must survive a round-trip through the URL
 * unchanged and would otherwise be flattened to the empty string.
 */
export function slugifyName(name: string): string {
  if (name === '__all__') return name
  return generateSlugFromName(name)
}

/**
 * Check whether a scalar field value differs from the global defaults.
 * Used to decide if a URL param should be treated as "authoritative"
 * (i.e., should bypass session fallback).
 */
function isNonDefaultValue<K extends keyof typeof DEFAULTS>(
  key: K,
  value: (typeof DEFAULTS)[K],
): boolean {
  return value !== DEFAULTS[key]
}

/** Minimal list info needed for slug resolution */
export interface ListRef {
  id: string
  name: string
  isSystem: boolean
}

/**
 * Resolve URL slugs against user's lists.
 * Returns matched list IDs and missing slug strings.
 * '__all__' passes through to matched without lookup.
 */
export function resolveListSlugs(
  slugs: string[],
  userLists: ListRef[],
): { matched: string[]; missing: string[] } {
  const unique = [...new Set(slugs.filter(Boolean))]
  const matched: string[] = []
  const missing: string[] = []

  // Build slug → list ID lookup
  const slugToId = new Map<string, string>()
  for (const list of userLists) {
    slugToId.set(slugifyName(list.name), list.id)
  }

  for (const slug of unique) {
    if (slug === '__all__') {
      matched.push('__all__')
    } else if (slugToId.has(slug)) {
      matched.push(slugToId.get(slug)!)
    } else {
      missing.push(slug)
    }
  }

  return { matched, missing }
}

/** Full state shape that gets serialized/deserialized */
export interface ProblemsPageState {
  searchQuery: string
  matchMode: 'any' | 'all'
  filters: FilterValues
  sortBy: string
  sortDir: 'asc' | 'desc'
  showTags: boolean
}

/**
 * Runtime schema for state read back from sessionStorage. The stored blob
 * crosses a trust boundary — a stale tab from an older deploy, a devtools or
 * extension edit, or any write that bypassed `writeSessionState` can leave a
 * value that doesn't match `ProblemsPageState`. Validate rather than blindly
 * cast, so a malformed `searchQuery` (etc.) is rejected here instead of
 * reaching `searchProblems`' `query.trim()` and crashing the problems list.
 * Kept in sync with the interface above.
 */
const filterStateSchema = z.object({
  operator: z.enum(['is', 'is not']),
  values: z.array(z.string()),
})

export const problemsPageStateSchema = z.object({
  searchQuery: z.string(),
  matchMode: z.enum(['any', 'all']),
  filters: z.object({
    status: filterStateSchema,
    difficulty: filterStateSchema,
    topics: filterStateSchema,
    lists: filterStateSchema,
  }),
  sortBy: z.string(),
  sortDir: z.enum(['asc', 'desc']),
  showTags: z.boolean(),
})

export const DEFAULTS: ProblemsPageState = {
  searchQuery: '',
  matchMode: 'all',
  filters: {
    status: { operator: 'is', values: [] },
    difficulty: { operator: 'is', values: [] },
    topics: { operator: 'is', values: [] },
    lists: { operator: 'is', values: [] },
  },
  sortBy: 'questionId',
  sortDir: 'asc',
  showTags: false,
}

/** Returns a fresh copy of DEFAULTS with deeply-copied filters to prevent mutation. */
function freshDefaults(): ProblemsPageState {
  return {
    ...DEFAULTS,
    filters: {
      status: { operator: 'is', values: [] },
      difficulty: { operator: 'is', values: [] },
      topics: { operator: 'is', values: [] },
      lists: { operator: 'is', values: [] },
    },
  }
}

/** Map from internal status values to URL-safe values */
function statusToUrl(value: string): string {
  return value.replace(/ /g, '-')
}

/** Map from internal operator to URL-safe operator */
function operatorToUrl(op: FilterOperator): string {
  return op.replace(/ /g, '-')
}

/**
 * Query param keys that `serializeToParams` writes. Exported so callers can
 * merge filter/sort params into a URL without clobbering unrelated params
 * owned by the page (e.g. `problem`, `list`, `tab` on /editor).
 */
export const MANAGED_PARAM_KEYS = [
  'sort',
  'dir',
  'difficulty',
  'difficulty_op',
  'status',
  'status_op',
  'topics',
  'topics_op',
  'lists',
  'lists_op',
  'match',
  'q',
  'tags',
] as const

/**
 * Merge managed filter/sort params into an existing search string, preserving
 * any params the hook does not own. The hook clears its managed keys from the
 * existing search and overlays the freshly-serialized values.
 */
export function mergeManagedParams(
  currentSearch: string,
  managed: URLSearchParams,
): URLSearchParams {
  const merged = new URLSearchParams(currentSearch)
  for (const key of MANAGED_PARAM_KEYS) merged.delete(key)
  for (const [key, value] of managed) merged.set(key, value)
  return merged
}

/**
 * Serialize current state to URL search params.
 * Only includes values that differ from defaults.
 * List UUIDs are converted to slugified names; difficulty is lowercased;
 * status spaces become hyphens; operators 'is not' → 'is-not'.
 */
export function serializeToParams(
  state: ProblemsPageState,
  userLists: ListRef[],
): URLSearchParams {
  const params = new URLSearchParams()

  // Sort
  if (state.sortBy !== DEFAULTS.sortBy) {
    params.set('sort', state.sortBy)
  }
  if (state.sortDir !== DEFAULTS.sortDir) {
    params.set('dir', state.sortDir)
  }

  // Filters
  const { filters } = state

  if (filters.difficulty.values.length > 0) {
    params.set(
      'difficulty',
      filters.difficulty.values.map((v) => v.toLowerCase()).join(','),
    )
    if (filters.difficulty.operator !== 'is') {
      params.set('difficulty_op', operatorToUrl(filters.difficulty.operator))
    }
  }

  if (filters.status.values.length > 0) {
    params.set('status', filters.status.values.map(statusToUrl).join(','))
    if (filters.status.operator !== 'is') {
      params.set('status_op', operatorToUrl(filters.status.operator))
    }
  }

  if (filters.topics.values.length > 0) {
    params.set('topics', filters.topics.values.map(slugifyName).join(','))
    if (filters.topics.operator !== 'is') {
      params.set('topics_op', operatorToUrl(filters.topics.operator))
    }
  }

  if (filters.lists.values.length > 0) {
    // Convert list UUIDs to slugified names; pass __all__ through.
    // Unknown IDs are dropped to avoid leaking internal identifiers in URLs.
    const idToSlug = new Map<string, string>()
    for (const list of userLists) {
      idToSlug.set(list.id, slugifyName(list.name))
    }
    const slugs = filters.lists.values.flatMap((id) => {
      if (id === '__all__') return ['__all__']
      const slug = idToSlug.get(id)
      return slug ? [slug] : []
    })
    if (slugs.length > 0) {
      params.set('lists', slugs.join(','))
      if (filters.lists.operator !== 'is') {
        params.set('lists_op', operatorToUrl(filters.lists.operator))
      }
    }
  }

  // Match mode
  if (state.matchMode !== DEFAULTS.matchMode) {
    params.set('match', state.matchMode)
  }

  // Search
  if (state.searchQuery !== DEFAULTS.searchQuery) {
    params.set('q', state.searchQuery)
  }

  // Show tags
  if (state.showTags !== DEFAULTS.showTags) {
    params.set('tags', String(state.showTags))
  }

  return params
}

/** Partial state returned by deserialization — only fields present in the URL */
export interface PartialProblemsState {
  sortBy?: string
  sortDir?: 'asc' | 'desc'
  filters?: {
    difficulty?: { operator: FilterOperator; values: string[] }
    status?: { operator: FilterOperator; values: string[] }
    topics?: { operator: FilterOperator; values: string[] }
    lists?: { operator: FilterOperator; values: string[] }
  }
  matchMode?: 'any' | 'all'
  searchQuery?: string
  showTags?: boolean
}

/** Map URL-safe operator back to internal FilterOperator */
function urlToOperator(val: string): FilterOperator | null {
  if (val === 'is') return 'is'
  if (val === 'is-not') return 'is not'
  return null
}

/** Map URL-safe status back to internal status value */
function urlToStatus(val: string): string {
  return val.replace(/-/g, ' ')
}

/** Capitalize first letter of each word (for difficulty) */
function capitalize(val: string): string {
  return val.charAt(0).toUpperCase() + val.slice(1)
}

/** Parse comma-separated param, strip empty, deduplicate */
function parseCommaSeparated(value: string): string[] {
  return [...new Set(value.split(',').filter(Boolean))]
}

/**
 * Deserialize URL search params into a partial state object.
 * Returns null if no recognized params are present.
 * Does NOT validate sort option values — that's context-specific (hook's job).
 * Difficulty values are capitalized; status hyphens → spaces; operators hyphens → spaces.
 * Topics and lists values are returned as raw slugs for later resolution.
 */
export function deserializeFromParams(
  params: URLSearchParams,
): PartialProblemsState | null {
  const result: PartialProblemsState = {}
  let hasAny = false

  // Sort
  const sort = params.get('sort')
  if (sort) {
    result.sortBy = sort
    hasAny = true
  }

  const dir = params.get('dir')
  if (dir === 'asc' || dir === 'desc') {
    result.sortDir = dir
    hasAny = true
  }

  // Match mode
  const match = params.get('match')
  if (match === 'any' || match === 'all') {
    result.matchMode = match
    hasAny = true
  }

  // Search
  const q = params.get('q')
  if (q != null && q !== '') {
    // URLSearchParams.get() already decodes — do NOT double-decode
    result.searchQuery = q.slice(0, 60)
    hasAny = true
  }

  // Show tags
  const tags = params.get('tags')
  if (tags === 'true' || tags === 'false') {
    result.showTags = tags === 'true'
    hasAny = true
  }

  // Filters
  const filters: PartialProblemsState['filters'] = {}
  let hasFilters = false

  // Difficulty
  const difficulty = params.get('difficulty')
  if (difficulty) {
    const op = urlToOperator(params.get('difficulty_op') ?? 'is') ?? 'is'
    filters.difficulty = {
      operator: op,
      values: [...new Set(parseCommaSeparated(difficulty).map(capitalize))],
    }
    hasFilters = true
  }

  // Status
  const status = params.get('status')
  if (status) {
    const op = urlToOperator(params.get('status_op') ?? 'is') ?? 'is'
    filters.status = {
      operator: op,
      values: [...new Set(parseCommaSeparated(status).map(urlToStatus))],
    }
    hasFilters = true
  }

  // Topics (raw slugs — resolved by hook against available topics)
  const topics = params.get('topics')
  if (topics) {
    const op = urlToOperator(params.get('topics_op') ?? 'is') ?? 'is'
    filters.topics = {
      operator: op,
      values: parseCommaSeparated(topics),
    }
    hasFilters = true
  }

  // Lists (raw slugs — resolved by hook against user lists)
  const lists = params.get('lists')
  if (lists) {
    const op = urlToOperator(params.get('lists_op') ?? 'is') ?? 'is'
    filters.lists = {
      operator: op,
      values: parseCommaSeparated(lists),
    }
    hasFilters = true
  }

  if (hasFilters) {
    result.filters = filters
    hasAny = true
  }

  return hasAny ? result : null
}

/**
 * Read and parse state from sessionStorage. Returns null on missing, corrupt,
 * or shape-invalid data.
 *
 * `validate` runs against the parsed JSON and is the trust boundary: the stored
 * blob is untrusted (stale tabs, devtools/extension edits, shape drift across
 * deploys), so callers pass a schema check that returns the typed value on a
 * match and `null` otherwise. Taking `T` from `validate`'s return — rather than
 * as a bare, caller-asserted type argument — is what makes the cast safe.
 */
export function readSessionState<T>(
  key: string,
  validate: (raw: unknown) => T | null,
): T | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = sessionStorage.getItem(key)
    if (!stored) return null
    return validate(JSON.parse(stored))
  } catch {
    return null
  }
}

/**
 * Write state to sessionStorage as JSON.
 */
export function writeSessionState<T>(key: string, state: T): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(key, JSON.stringify(state))
  } catch {
    // quota exceeded or other storage error — silently ignore
  }
}

/** Resolve topic slugs against available topic display names */
function resolveTopicSlugs(
  slugs: string[],
  availableTopics: string[],
): string[] {
  const slugToName = new Map<string, string>()
  for (const topic of availableTopics) {
    slugToName.set(slugifyName(topic), topic)
  }
  return slugs
    .map((slug) => slugToName.get(slug))
    .filter((name): name is string => name != null)
}

/**
 * Resolve final state from URL params, sessionStorage, and defaults.
 * Priority: URL > sessionStorage > defaults.
 *
 * When URL params exist, they fully replace sessionStorage (not merged).
 * Only the fields present in URL params are applied; remaining fields use defaults.
 *
 * Topic slugs are resolved against availableTopics.
 * List slugs are resolved against userLists; unresolved go into missingLists.
 * Invalid sort options are dropped (fall through to session/default).
 */
export function resolveState(
  urlState: PartialProblemsState | null,
  sessionState: ProblemsPageState | null,
  userLists: ListRef[],
  availableTopics: string[],
  validSortOptions: string[],
): { state: ProblemsPageState; missingLists: string[] } {
  let missingLists: string[] = []

  // No URL params — use session or defaults
  if (!urlState) {
    const base = sessionState ?? DEFAULTS
    const defaults = freshDefaults()
    // Sanitize sortBy/sortDir from session against valid options
    const sanitizedSortBy =
      base.sortBy && validSortOptions.includes(base.sortBy)
        ? base.sortBy
        : DEFAULTS.sortBy
    const sanitizedSortDir =
      base.sortDir === 'asc' || base.sortDir === 'desc'
        ? base.sortDir
        : DEFAULTS.sortDir
    return {
      state: {
        ...DEFAULTS,
        ...base,
        sortBy: sanitizedSortBy,
        sortDir: sanitizedSortDir,
        filters: {
          status: { ...defaults.filters.status, ...base.filters?.status },
          difficulty: {
            ...defaults.filters.difficulty,
            ...base.filters?.difficulty,
          },
          topics: { ...defaults.filters.topics, ...base.filters?.topics },
          lists: { ...defaults.filters.lists, ...base.filters?.lists },
        },
      },
      missingLists: [],
    }
  }

  // URL params present — validate and apply. If no params survive validation,
  // fall through to session/defaults (e.g., ?sort=custom on /problems).
  const state = freshDefaults()
  let hasValidParam = false

  // Sort — validate against allowed options
  if (urlState.sortBy && validSortOptions.includes(urlState.sortBy)) {
    state.sortBy = urlState.sortBy
    if (isNonDefaultValue('sortBy', urlState.sortBy)) {
      hasValidParam = true
    }
  }
  if (urlState.sortDir) {
    state.sortDir = urlState.sortDir
    if (isNonDefaultValue('sortDir', urlState.sortDir)) {
      hasValidParam = true
    }
  }

  // Match mode
  if (urlState.matchMode) {
    state.matchMode = urlState.matchMode
    if (isNonDefaultValue('matchMode', urlState.matchMode)) {
      hasValidParam = true
    }
  }

  // Search
  if (urlState.searchQuery != null) {
    state.searchQuery = urlState.searchQuery
    if (isNonDefaultValue('searchQuery', urlState.searchQuery)) {
      hasValidParam = true
    }
  }

  // Show tags
  if (urlState.showTags != null) {
    state.showTags = urlState.showTags
    if (isNonDefaultValue('showTags', urlState.showTags)) {
      hasValidParam = true
    }
  }

  // Filters — each field independently from URL or defaults
  if (urlState.filters?.difficulty) {
    state.filters.difficulty = urlState.filters.difficulty
    hasValidParam = true
  }
  if (urlState.filters?.status) {
    state.filters.status = urlState.filters.status
    hasValidParam = true
  }
  if (urlState.filters?.topics) {
    const resolvedValues = resolveTopicSlugs(
      urlState.filters.topics.values,
      availableTopics,
    )
    if (resolvedValues.length > 0) {
      state.filters.topics = {
        operator: urlState.filters.topics.operator,
        values: resolvedValues,
      }
      hasValidParam = true
    }
  }
  if (urlState.filters?.lists) {
    const resolved = resolveListSlugs(urlState.filters.lists.values, userLists)
    if (resolved.matched.length > 0) {
      state.filters.lists = {
        operator: urlState.filters.lists.operator,
        values: resolved.matched,
      }
      hasValidParam = true
    }
    missingLists = resolved.missing
  }

  // If every URL param was invalid/dropped, treat as "no URL params" — use session
  if (!hasValidParam) {
    const base = sessionState ?? DEFAULTS
    const defaults = freshDefaults()
    // Sanitize sortBy/sortDir from session against valid options
    const sanitizedSortBy =
      base.sortBy && validSortOptions.includes(base.sortBy)
        ? base.sortBy
        : DEFAULTS.sortBy
    const sanitizedSortDir =
      base.sortDir === 'asc' || base.sortDir === 'desc'
        ? base.sortDir
        : DEFAULTS.sortDir
    return {
      state: {
        ...DEFAULTS,
        ...base,
        sortBy: sanitizedSortBy,
        sortDir: sanitizedSortDir,
        filters: {
          status: { ...defaults.filters.status, ...base.filters?.status },
          difficulty: {
            ...defaults.filters.difficulty,
            ...base.filters?.difficulty,
          },
          topics: { ...defaults.filters.topics, ...base.filters?.topics },
          lists: { ...defaults.filters.lists, ...base.filters?.lists },
        },
      },
      missingLists,
    }
  }

  return { state, missingLists }
}
